/**
 * v19 Canary Rollout 灰度发布管理
 *
 * 策略: 按 userCode hash 决定进入哪一批
 *   - 5% canary  (5/21-5/25 · 5 灯塔客户 9 员工)
 *   - 25% beta   (5/26-5/31 · 44 员工)
 *   - 50% pilot  (6/1-6/15  · 88 员工)
 *   - 100% GA    (6/16+     · 175 员工)
 *
 * 配置存于 localStorage tnCanaryConfig OR Worker /api/canary/config 远程读
 * GM 可强制全员开关 (override_to: 'v18' | 'v19' | 'auto')
 */

export type CanaryStage = 'canary' | 'beta' | 'pilot' | 'ga'
export type RolloutMode = 'auto' | 'v18' | 'v19'

export interface CanaryConfig {
  stage: CanaryStage
  rollout_pct: number // 0-100
  override_mode: RolloutMode // GM 可强制
  cohort_seed: string // 哈希 salt 防偏向
  effective_from: string // ISO date
}

const DEFAULT: CanaryConfig = {
  stage: 'canary',
  rollout_pct: 5,
  override_mode: 'auto',
  cohort_seed: 'tn-v19-2026',
  effective_from: '2026-05-21',
}

// djb2 hash (一致性 hash · 用户分桶用)
function hash(str: string): number {
  let h = 5381
  for (let i = 0; i < str.length; i++) h = ((h << 5) + h) ^ str.charCodeAt(i)
  return Math.abs(h)
}

export function getCanaryConfig(): CanaryConfig {
  try {
    const stored = localStorage.getItem('tnCanaryConfig')
    if (stored) return { ...DEFAULT, ...JSON.parse(stored) }
  } catch { /* ignore */ }
  return DEFAULT
}

export function setCanaryConfig(cfg: Partial<CanaryConfig>) {
  const next = { ...getCanaryConfig(), ...cfg }
  localStorage.setItem('tnCanaryConfig', JSON.stringify(next))
  return next
}

/**
 * 判断当前用户是否进入 v19
 * @param userCode 员工 code (如 GM01 / SALES-01 / W-001)
 * @returns true = v19 · false = v18 fallback
 */
export function isUserInV19(userCode: string): boolean {
  const cfg = getCanaryConfig()

  // GM 强制 override (最高优先 · 含 v18 强制回滚 · 灯塔也会被拉回 v18)
  if (cfg.override_mode === 'v19') return true
  if (cfg.override_mode === 'v18') return false

  // 100% GA · 所有人 v19
  if (cfg.rollout_pct >= 100) return true

  // 灯塔客户白名单 (即使 0% rollout 也永远 v19, 第一时间反馈)
  const LIGHTHOUSE = ['GM01', 'SALES-01', 'ENG-01', 'W-001', 'FIN-01']
  if (LIGHTHOUSE.includes(userCode)) return true

  // 0% rollout · 灯塔之外的人都 v18
  if (cfg.rollout_pct <= 0) return false

  // 一致性 hash 分桶
  const bucket = hash(userCode + cfg.cohort_seed) % 100
  return bucket < cfg.rollout_pct
}

/**
 * Stage 自动推进 (按日期)
 * 可由 GM 手动覆盖
 */
export function autoAdvanceStage(today: Date = new Date()): CanaryConfig {
  const cfg = getCanaryConfig()
  if (cfg.override_mode !== 'auto') return cfg

  const milestones: Array<{ date: string; stage: CanaryStage; pct: number }> = [
    { date: '2026-05-21', stage: 'canary', pct: 5 },
    { date: '2026-05-26', stage: 'beta', pct: 25 },
    { date: '2026-06-01', stage: 'pilot', pct: 50 },
    { date: '2026-06-16', stage: 'ga', pct: 100 },
  ]

  let target = milestones[0]
  for (const m of milestones) {
    if (today >= new Date(m.date)) target = m
  }

  if (cfg.stage !== target.stage || cfg.rollout_pct !== target.pct) {
    return setCanaryConfig({ stage: target.stage, rollout_pct: target.pct })
  }
  return cfg
}

/**
 * 紧急回滚 (Jack 红色按钮 · GM 权限)
 */
export function emergencyRollback(reason: string) {
  setCanaryConfig({ override_mode: 'v18', rollout_pct: 0 })
  console.error('[v19 CANARY] 🚨 紧急回滚到 v18 · 原因:', reason)
  // 可选: 上报 Sentry + 通知 Worker
}

/**
 * 获取当前 stage 用户分布预估
 */
export function getStageStats() {
  const cfg = getCanaryConfig()
  const total = 175 // 当前总员工数 (90 + 待扩 85)
  const inV19 = Math.round(total * cfg.rollout_pct / 100)
  return {
    stage: cfg.stage,
    rollout_pct: cfg.rollout_pct,
    estimated_v19_users: inV19,
    estimated_v18_users: total - inV19,
    override: cfg.override_mode,
  }
}
